import {Link} from '../../Link';
import React from 'react';
import {graphql, useStaticQuery} from 'gatsby';
import {LocaleEnum, localeToLocaleAlias} from '../../../types/locale';
import {useIntl} from 'react-intl';
import {DatoException} from '../../../utils/exception/DatoException';
import {forceTrailingSlash} from '../../../utils/urlUtils';
import {localeToPath} from '../../../utils/localeToPath';
import {CustomFooterQueryType} from '../../../types/block';

export const CustomFooter: React.FC = () => {
  const intl = useIntl();

  const allLocalesFooterData: CustomFooterQueryType = useStaticQuery(graphql`
    fragment Footer on DatoCmsFooter {
      title
      originalId
      model {
        originalId
      }
      sections {
        title
        links {
          label
          link {
            ... on DatoCmsBlogpost {
              slug
            }
            ... on DatoCmsGlossaryItemPage {
              id
              slug
            }
            ... on DatoCmsPage {
              slug
            }
          }
          customLink
        }
      }
    }

    query ActiveFooter {
      deAT: allDatoCmsActiveFooter(filter: {_allActiveFooterLocales: {elemMatch: {locale: {eq: "de-AT"}}}}, locale: "de-AT") {
        nodes {
          activeFooter {
            ...Footer
          }
        }
      }
      deCH: allDatoCmsActiveFooter(filter: {_allActiveFooterLocales: {elemMatch: {locale: {eq: "de-CH"}}}}, locale: "de-CH") {
        nodes {
          activeFooter {
            ...Footer
          }
        }
      }
      deDE: allDatoCmsActiveFooter(filter: {_allActiveFooterLocales: {elemMatch: {locale: {eq: "de-DE"}}}}, locale: "de-DE") {
        nodes {
          activeFooter {
            ...Footer
          }
        }
      }
      enIE: allDatoCmsActiveFooter(filter: {_allActiveFooterLocales: {elemMatch: {locale: {eq: "en-IE"}}}}, locale: "en-IE") {
        nodes {
          activeFooter {
            ...Footer
          }
        }
      }
      enUS: allDatoCmsActiveFooter(filter: {_allActiveFooterLocales: {elemMatch: {locale: {eq: "en-US"}}}}, locale: "en-US") {
        nodes {
          activeFooter {
            ...Footer
          }
        }
      }
    }
  `);

  const localeAlias = localeToLocaleAlias(intl.locale as LocaleEnum);
  const localeFooterData = allLocalesFooterData[localeAlias].nodes[0].activeFooter;

  if (localeFooterData.sections.length === 0) {
    throw new DatoException(
      `The Active Footer \`${localeFooterData.title}\` has no \`${intl.locale}\` locale`,
      localeFooterData.model.originalId,
      localeFooterData.originalId
    );
  }

  return (
    <div data-testid='custom-footer' className='grid sm:grid-cols-3 xl:grid-cols-6 mt-4'>
      {localeFooterData.sections.map((section, index) => (
        <section key={`${section.title}-${index}`} className='col-span-1 pr-4'>
          <p className='text-white font-body text-base font-semibold py-1'>{section.title}</p>
          <ul className='mb-10'>
            {section.links.map((link, index) => {
              let href = link.customLink;
              if (link.link?.slug) {
                // These are links to pages, they can't
                // be treated as relative
                href = forceTrailingSlash(`/${localeToPath(intl.locale as LocaleEnum)}/${link.link.slug}`);
              }

              if (!href) {
                throw new DatoException(
                  `The section \`${section.title}\` in the Active Footer \`${localeFooterData.title}\` has a link \`${link.label}\` with no \`${intl.locale}\` locale`,
                  localeFooterData.model.originalId,
                  localeFooterData.originalId
                );
              }

              return (
                <li key={link.label + '-' + index}>
                  <Link className='text-white font-body hover:underline xl:pr-10 block py-2 leading-snug' to={href}>
                    {link.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};
